import { createSelector } from 'redux-orm';

import orm from '../orm';
import { selectPath } from './router';
import { selectCurrentUserId, selectEffectivePermissionsForCard } from './users';
import { isLocalId } from '../utils/local-id';
import { RelationTypes } from '../constants/Enums';

export const makeSelectCardById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return {
        ...cardModel.ref,
        coverUrl: cardModel.coverAttachment && cardModel.coverAttachment.coverUrl,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const selectCardById = makeSelectCardById();

export const makeSelectCardIndexById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      const cardModels = cardModel.list.getFilteredOrderedCardsModelArray();
      return cardModels.findIndex((cardModelItem) => cardModelItem.id === cardModel.id);
    },
  );

export const selectCardIndexById = makeSelectCardIndexById();

export const makeSelectUsersByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.users.toRefArray();
    },
  );

export const selectUsersByCardId = makeSelectUsersByCardId();

export const makeSelectLabelsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.labels.toRefArray();
    },
  );

export const selectLabelsByCardId = makeSelectLabelsByCardId();

export const makeSelectRelationsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }
      return cardModel.outgoingRelations.toModelArray().map((relationModel) => ({
        ...relationModel.ref,

        card: relationModel.card2?.ref,
      }));
    },
  );

export const selectRelationsByCardId = makeSelectRelationsByCardId();

export const makeSelectIncomingRelationsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return null;
      }

      return cardModel.incomingRelations.toModelArray().map((relationModel) => ({
        ...relationModel.ref,

        card: relationModel.card1?.ref,
      }));
    },
  );

export const selectIncomingRelationsByCardId = makeSelectIncomingRelationsByCardId();

export const makeSelectParentCardByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);
      if (!cardModel) return null;

      // outgoing SUB_TASK => 'card2' est le PARENT (cohérent avec makeSelectRelationsByCardId)
      const parentRelation = cardModel.outgoingRelations
        .toModelArray()
        .find((rel) => rel.type === RelationTypes.SUB_TASK);

      if (!parentRelation || !parentRelation.card2) return null;

      const { id: parentId, name } = parentRelation.card2.ref;
      return { id: parentId, name };
    },
  );

export const selectParentCardByCardId = makeSelectParentCardByCardId();

export const selectParentCardForCurrentCard = (state) =>
  selectParentCardByCardId(state, selectPath(state).cardId);

export const makeSelectAttachmentsTotalByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.attachments.count();
    },
  );

export const selectAttachmentsTotalByCardId = makeSelectAttachmentsTotalByCardId();

export const makeSelectLastActivityIdByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      const lastActivityModel = cardModel.getFilteredOrderedInCardActivitiesQuerySet().last();

      return lastActivityModel && lastActivityModel.id;
    },
  );

export const selectLastActivityIdByCardId = makeSelectLastActivityIdByCardId();

export const makeSelectNotificationsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getUnreadNotificationsQuerySet().toRefArray();
    },
  );

export const selectNotificationsByCardId = makeSelectNotificationsByCardId();

export const makeSelectNotificationsTotalByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getUnreadNotificationsQuerySet().count();
    },
  );

export const selectNotificationsTotalByCardId = makeSelectNotificationsTotalByCardId();

export const makeSelectProgressByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card, List, Board }, id) => {
      const rootCard = Card.withId(id);
      if (!rootCard) return null;

      const board = rootCard.boardId ? Board.withId(rootCard.boardId) : null;
      if (!board) return null;

      // colonnes du board ordonnées par position
      const orderedLists = board.lists.orderBy('position').toRefArray();
      if (orderedLists.length === 0) return null;

      const minPos = orderedLists[0].position;

      // position de la colonne "terminé" ; fallback = dernière colonne
      const doneList = board.doneListId ? List.withId(board.doneListId) : null;
      const donePos = doneList ? doneList.position : orderedLists[orderedLists.length - 1].position;

      const scale = donePos - minPos; // dénominateur de normalisation

      // avancement (0..1) d'une feuille selon sa colonne, plafonné à 1 dès doneListId
      const leafProgress = (cardModel) => {
        if (!cardModel.listId) return 0;
        const list = List.withId(cardModel.listId);
        if (!list) return 0;
        if (list.position >= donePos) return 1; // >= "terminé" => 100%
        if (scale <= 0) return 0;
        return (list.position - minPos) / scale; // progression normalisée
      };

      const visited = new Set();
      let sum = 0;
      let count = 0;

      // parcourt la descendance SUB_TASK (via incoming -> card1) et n'agrège que les FEUILLES
      const walk = (cardModel) => {
        if (!cardModel || visited.has(cardModel.id)) return;
        visited.add(cardModel.id);

        const children = cardModel.incomingRelations
          .toModelArray()
          .filter((rel) => rel.type === RelationTypes.SUB_TASK)
          .map((rel) => rel.card1)
          .filter(Boolean);

        if (children.length === 0) {
          // feuille : contribue par sa propre colonne
          sum += leafProgress(cardModel);
          count += 1;
          return;
        }

        children.forEach(walk);
      };

      // une carte n'a un % que si elle a au moins un enfant SUB_TASK
      const rootChildren = rootCard.incomingRelations
        .toModelArray()
        .filter((rel) => rel.type === RelationTypes.SUB_TASK);

      if (rootChildren.length === 0) return null; // simple sous-tâche => pas de %

      walk(rootCard);

      if (count === 0) return null;
      return Math.round((sum / count) * 100);
    },
  );

export const selectProgressByCardId = makeSelectProgressByCardId();
export const selectProgressForCurrentCard = (state) =>
  selectProgressByCardId(state, selectPath(state).cardId);

export const selectCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel.ref;
  },
);

export const selectUsersForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel.users.toRefArray();
  },
);

export const selectLabelsForCurrentCard = (state) =>
  selectLabelsByCardId(state, selectPath(state).cardId);

export const selectRelationsForCurrentCard = (state) =>
  selectRelationsByCardId(state, selectPath(state).cardId);

export const selectIncomingRelationsForCurrentCard = (state) =>
  selectIncomingRelationsByCardId(state, selectPath(state).cardId);

export const selectAttachmentsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getOrderedAttachmentsQuerySet()
      .toRefArray()
      .map((attachment) => ({
        ...attachment,
        isCover: attachment.id === cardModel.coverAttachmentId,
        isPersisted: !isLocalId(attachment.id),
      }));
  },
);

export const selectActivitiesForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  (state) => selectCurrentUserId(state),
  ({ Card }, id, currentUserId) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getFilteredOrderedInCardActivitiesQuerySet()
      .toModelArray()
      .map((activityModel) => ({
        ...activityModel.ref,
        isPersisted: !isLocalId(activityModel.id),
        user: {
          ...activityModel.user.ref,
          isCurrent: activityModel.user.id === currentUserId,
        },
      }));
  },
);

export const selectNotificationIdsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getUnreadNotificationsQuerySet()
      .toRefArray()
      .map((notification) => notification.id);
  },
);

export const makeSelectCardNamesByIds = () =>
  createSelector(
    orm,
    (_, ids) => ids,
    ({ Card }, ids) => {
      if (!ids || ids.length === 0) {
        return {};
      }

      const result = {};
      ids.forEach((id) => {
        const cardModel = Card.withId(id);
        if (cardModel) {
          result[id] = cardModel.name;
        }
      });
      return result;
    },
  );

export const selectCardNamesByIds = makeSelectCardNamesByIds();

export const selectCanCommentCurrentCard = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  (state) => selectPath(state).cardId,

  ({ Card, UserRoleAssignment, RolePermission, Permission }, currentUserId, cardId) => {
    const permissions = selectEffectivePermissionsForCard.resultFunc(
      {
        Card,
        UserRoleAssignment,
        RolePermission,
        Permission,
      },
      currentUserId,
      cardId,
    );

    return permissions.includes('comment_create');
  },
);

export default {
  makeSelectCardById,
  selectCardById,
  makeSelectCardIndexById,
  selectCardIndexById,
  makeSelectUsersByCardId,
  selectUsersByCardId,
  makeSelectLabelsByCardId,
  selectLabelsByCardId,
  makeSelectAttachmentsTotalByCardId,
  selectAttachmentsTotalByCardId,
  makeSelectLastActivityIdByCardId,
  selectLastActivityIdByCardId,
  makeSelectNotificationsByCardId,
  selectNotificationsByCardId,
  makeSelectNotificationsTotalByCardId,
  selectNotificationsTotalByCardId,
  selectCurrentCard,
  selectUsersForCurrentCard,
  selectLabelsForCurrentCard,
  selectRelationsForCurrentCard,
  selectIncomingRelationsForCurrentCard,
  makeSelectParentCardByCardId,
  selectParentCardByCardId,
  selectParentCardForCurrentCard,
  selectAttachmentsForCurrentCard,
  selectActivitiesForCurrentCard,
  selectNotificationIdsForCurrentCard,
  makeSelectRelationsByCardId,
  selectRelationsByCardId,
  selectIncomingRelationsByCardId,
  makeSelectIncomingRelationsByCardId,
  makeSelectProgressByCardId,
  selectProgressByCardId,
  selectProgressForCurrentCard,
  makeSelectCardNamesByIds,
  selectCardNamesByIds,
  selectCanCommentCurrentCard,
};
